import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { stripMemberReferencesToPid } from '../../../pedigree-tool/pedigree/edgeIntegrity';

// UNDO FOR AN ADD BATCH — and why it is real work rather than a state rollback.
//
// POST /pedigree-config is UPSERT-ONLY: it never deletes a member absent from the
// posted array. The save fires within ~300ms of an add, so by the time the
// confirmation is on screen the created nodes are already persisted as
// FamilyChanged documents. Dropping them from local state would leave those
// documents orphaned, and the next save would not remove them.
//
// So a persisted node needs POST /pedigree/delete/:user_id/:familyMemberId — one
// call each, a hard delete that also strips edges server-side, drops the pid from
// the config and deletes the node's PathwayInstance / PathwayEvent rows. It is
// keyed on familyMemberId, which only exists once the save echo has backfilled
// it; before that, local removal alone is correct and sufficient.

/** Anything created that has since acquired descendants or a spouse of its own. */
export const nodesWithNewDependants = (members, createdPids) => {
  const created = new Set(createdPids.map(String));
  return (members || []).filter((m) => {
    if (!created.has(String(m.pid))) return false;
    const kids = Array.isArray(m.childrenIds) ? m.childrenIds : [];
    const spouses = Array.isArray(m.spouseIds) ? m.spouseIds : [];
    const pregnancies = Array.isArray(m.pregnancies) ? m.pregnancies : [];
    return kids.length > 0 || spouses.length > 0 || pregnancies.length > 0;
  });
};

const nameOf = (m) =>
  [m?.firstName, m?.lastName].filter(Boolean).join(' ') ||
  `UPN ${m?.upn ?? m?.pid}`;

const reasonFor = (blocker) => {
  const kids = Array.isArray(blocker.childrenIds) ? blocker.childrenIds : [];
  const spouses = Array.isArray(blocker.spouseIds) ? blocker.spouseIds : [];
  const what = kids.length
    ? 'a child'
    : spouses.length
    ? 'a spouse'
    : 'a pregnancy';
  return `Can't undo: ${nameOf(
    blocker
  )} now has ${what}. Delete them directly instead.`;
};

/**
 * Reverse a whole add batch, or refuse and change nothing.
 *
 * ATOMIC-OR-NOTHING, chosen deliberately. The brief allowed either refusing or
 * removing just the still-childless nodes; refusing the whole batch wins because
 * it is the only contract a clinician can trust — undo either fully reverses the
 * action or does nothing, and a half-undone batch leaves a state nobody asked
 * for. The fallback is the existing per-node delete dialog, which asks for the
 * UPN to be typed. Silently deleting a subtree from a clinical record is never
 * acceptable.
 *
 * @param {Object}   args
 * @param {Object}   args.batch          { createdIds: number[] }
 * @param {Array}    args.members        Latest memberRecords.
 * @param {Object}   args.user           Needs `_id`.
 * @param {Function} args.setMemberRecords
 * @param {boolean}  args.isSaving       True → refuse; see the sequencing note.
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export const undoAddBatch = async ({
  batch,
  members,
  user,
  setMemberRecords,
  isSaving
}) => {
  const createdPids = batch?.createdIds || [];
  if (!createdPids.length) return { ok: true };

  // SEQUENCING TRAP: the save POSTs memberRecordsRef.current. A delete landing
  // while a save is in flight could be followed by that save re-upserting the
  // node we just removed. Refusing while saving closes that window — and it costs
  // nothing, because decision D1 keeps the whole panel disabled until the save
  // settles anyway.
  if (isSaving) {
    return { ok: false, reason: 'Wait for the save to finish, then undo.' };
  }

  const blockers = nodesWithNewDependants(members, createdPids);
  if (blockers.length) return { ok: false, reason: reasonFor(blockers[0]) };

  // Newest first, so a parent is never deleted before a node that still points at
  // it — the server strips edges per delete and the order keeps each call's view
  // of the graph consistent.
  const ordered = [...createdPids].sort((a, b) => Number(b) - Number(a));

  for (const pid of ordered) {
    const record = (members || []).find((m) => String(m.pid) === String(pid));
    if (record?.familyMemberId) {
      await axios.post(
        APICONSTANTS.deletePedigreeFamilyMember
          .replace(':user_id', user._id)
          .replace(':member_id', record.familyMemberId)
      );
    }
  }

  // One functional update for the whole batch: state may have moved on while the
  // POSTs were in flight (save-echo identity backfill), so derive from `prev`
  // rather than the snapshot the guard above read.
  const removing = new Set(ordered.map(String));
  setMemberRecords((prev) => {
    let next = (prev || []).filter((p) => !removing.has(String(p.pid)));
    for (const pid of ordered) {
      next = next.map((p) => stripMemberReferencesToPid(p, Number(pid)));
    }
    return next;
  });

  return { ok: true };
};
